import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';
import Select from 'react-select';
import { useCountries } from '../../hooks/useCountries';
import { usePhoneNumber } from '../../hooks/usePhoneNumber';
import { useEligibleCountries } from '../../hooks/useEligibleCountries';

const FieldLabel = ({ children }) => (
  <label className="block text-[10px] uppercase tracking-[0.18em] font-semibold text-[#0A4730]/70 mb-2">
    {children}
  </label>
);

const INPUT_CLASS =
  "w-full rounded-2xl bg-white border border-slate-200 px-4 py-3.5 text-sm text-[#0A2540] placeholder:text-slate-400 focus:outline-none focus:border-[#0A4730] focus:ring-4 focus:ring-[#0A4730]/5 transition-all duration-200 shadow-sm";

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 50,
    borderRadius: 16,
    backgroundColor: "#FFFFFF",
    border: state.isFocused
      ? "1.5px solid #0A4730"
      : "1px solid #E2E8F0",
    boxShadow: state.isFocused
      ? "0 0 0 4px rgba(10, 71, 48, 0.08)"
      : "0 1px 2px rgba(0,0,0,0.03)",
    fontFamily: "inherit",
    fontSize: 14,
    paddingLeft: 4,
    cursor: "pointer",
    "&:hover": {
      borderColor: "#0A4730",
    },
    transition: "all 0.2s ease",
  }),

  menu: (base) => ({
    ...base,
    borderRadius: 16,
    overflow: "hidden",
    zIndex: 999,
    boxShadow: "0 8px 30px rgba(10,71,48,0.12)",
    border: "1px solid #0A473015",
  }),

  menuList: (base) => ({
    ...base,
    padding: 6,
    maxHeight: 220,
  }),

  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#0A4730"
      : state.isFocused
        ? "#F5F9F4"
        : "white",
    color: state.isSelected
      ? "white"
      : "#0A2540",
    borderRadius: 10,
    fontSize: 14,
    padding: "10px 12px",
    cursor: "pointer",
    transition: "background 0.1s",
  }),

  input: (base) => ({
    ...base,
    fontSize: 14,
    color: "#0A2540",
  }),

  placeholder: (base) => ({
    ...base,
    fontSize: 14,
    color: "#0A254050",
  }),

  singleValue: (base) => ({
    ...base,
    fontSize: 14,
    color: "#0A2540",
  }),

  indicatorSeparator: () => ({
    display: "none",
  }),

  dropdownIndicator: (base, state) => ({
    ...base,
    color: state.isFocused
      ? "#0A4730"
      : "#0A254040",
    transition: "color 0.15s, transform 0.2s",
    transform: state.selectProps.menuIsOpen
      ? "rotate(180deg)"
      : "rotate(0deg)",
    paddingRight: 12,
  }),

  noOptionsMessage: (base) => ({
    ...base,
    fontSize: 13,
    color: "#0A254060",
    padding: "12px 8px",
  }),
};

export function CountryStep({
  onNext,
  onBack,
  defaultValues,
}) {
  const navigate = useNavigate();

  const {
    countries,
    loading,
    error,
  } = useCountries();

  // Restrict to countries whose currency we can currently onramp from.
  const { eligibleCountries, countryOptions } =
    useEligibleCountries(countries);

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      countryCode:
        defaultValues?.country ?? '',
      phoneNumber:
        defaultValues?.phoneNumber ?? '',
    },
  });

  const selectedCode =
    watch('countryCode');

  /*
   * Centralized phone-number validation
   * and normalization.
   *
   * This is the same phone-number
   * implementation used by useCorridor.
   */
  const phoneNumber =
    usePhoneNumber(selectedCode);

  const selectedCountryOption =
    useMemo(
      () =>
        countryOptions.find(
          (c) =>
            c.value === selectedCode
        ) || null,
      [
        countryOptions,
        selectedCode,
      ]
    );

  const selectedCountry =
    useMemo(
      () =>
        eligibleCountries.find(
          (c) =>
            c.code === selectedCode
        ),
      [
        eligibleCountries,
        selectedCode,
      ]
    );

  const onSubmit = (data) => {
    const country =
      eligibleCountries.find(
        (c) =>
          c.code ===
          data.countryCode
      );

    if (!country) {
      return;
    }

    /*
     * Normalize using the same
     * centralized phone utility.
     *
     * The stored phoneNumber remains
     * the national/subscriber number.
     *
     * The stored phone is canonical E.164.
     */
    const nationalNumber =
      phoneNumber.toNationalNumber(
        data.phoneNumber,
        data.countryCode
      );

    const internationalNumber =
      phoneNumber.toE164(
        data.phoneNumber,
        data.countryCode
      );

    /*
     * Do not continue if normalization
     * failed for any reason.
     */
    if (
      !nationalNumber ||
      !internationalNumber
    ) {
      return;
    }

    onNext({
      country: country.code,
      countryName: country.name,
      currency: country.currency,
      dialCode: country.dialCode,

      // National/subscriber number
      phoneNumber: nationalNumber,

      // Canonical international number
      phone: internationalNumber,
    });
  };

  return (
    <div className="flex flex-col h-full w-full max-w-xl mx-auto bg-[#FDFDFD] p-5 md:p-8 text-left">
      <button
        onClick={onBack}
        className="mb-6 w-10 h-10 rounded-full border border-slate-200 bg-white flex items-center justify-center active:scale-95 transition text-[#0A4730]/60 hover:border-[#0A4730]/30 shadow-sm"
      >
        <ChevronLeft size={20} />
      </button>

      <div className="mb-8 text-center">
        <h1 className="text-2xl text-[#0A4730] font-serif font-semibold tracking-tight">
          Where are you sending from?
        </h1>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-6 flex-grow"
      >
        {/* Country Section Card */}
        <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)] space-y-3">
          <div>
            <FieldLabel>
              Country
            </FieldLabel>

            <Controller
              name="countryCode"
              control={control}
              rules={{
                required:
                  'Please select a country',
              }}
              render={({ field }) => (
                <Select
                  options={
                    countryOptions
                  }
                  styles={
                    selectStyles
                  }
                  value={
                    selectedCountryOption
                  }
                  onChange={(opt) =>
                    field.onChange(
                      opt
                        ? opt.value
                        : ''
                    )
                  }
                  isDisabled={loading}
                  isSearchable
                  placeholder={
                    loading
                      ? 'Loading countries…'
                      : 'Select a country'
                  }
                  noOptionsMessage={() =>
                    "No country found"
                  }
                />
              )}
            />

            {errors.countryCode && (
              <p className="text-xs text-red-500 mt-1.5">
                {
                  errors
                    .countryCode
                    .message
                }
              </p>
            )}

            {error && (
              <p className="text-xs text-red-500 mt-1.5">
                Couldn't load the
                country list — check
                your connection and try
                again.
              </p>
            )}
          </div>
        </div>

        {/* Phone Section Card */}
        <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)] space-y-3">
          <FieldLabel>
            Phone number
          </FieldLabel>

          <div className="flex gap-2">
            <div className="flex items-center justify-center bg-white rounded-2xl px-4 min-w-[76px] text-[#0A2540] font-semibold text-sm border border-slate-200 shadow-sm">
              {selectedCountry
                ? selectedCountry.dialCode
                : '+--'}
            </div>

            <input
              {...register(
                'phoneNumber',
                {
                  required:
                    'Phone number is required',

                  validate: (value) => {
                    if (!selectedCode) {
                      return 'Please select a country first';
                    }

                    const valid =
                      phoneNumber.validate(
                        value,
                        {
                          country:
                            selectedCode,
                          mobileOnly:
                            false,
                        }
                      );

                    return (
                      valid ||
                      'Enter a valid phone number'
                    );
                  },
                }
              )}
              placeholder="7700 900123"
              className={INPUT_CLASS}
            />
          </div>

          {errors.phoneNumber && (
            <p className="text-xs text-red-500 mt-1.5">
              {
                errors.phoneNumber
                  .message
              }
            </p>
          )}
        </div>

        <div className="pt-2">
          <button
            type="submit"
            className="w-full h-14 rounded-2xl bg-[#0A4730] text-white font-bold tracking-wide hover:bg-[#0A4730]/90 transition-all shadow-lg active:scale-[0.98]"
          >
            Continue
          </button>
        </div>
      </form>

      <div className="mt-8 text-center text-sm">
        <span className="text-slate-500">
          Have an account?{' '}
        </span>

        <button
          className="font-bold text-[#0A4730] hover:underline"
          onClick={() =>
            navigate('/login')
          }
        >
          Login
        </button>
      </div>
    </div>
  );
}